import * as React from 'react';
import {useCallback, useState} from 'react';
import {Alert, Chip, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, Paper} from '@mui/material';
import Button from '@mui/material/Button';
import {Formik, FormikErrors} from 'formik';
import {useAppTranslation} from 'services/i18n';
import {useAppDispatch} from 'store';
import {rejectApprovalRequest} from 'store/approvals';
import {addMessage} from 'store/localApp';
import {isApiResultError} from '../../helpers/api';
import {TextFormField} from 'components/form/TextFormField';
import {approvalDecisionError, approvalSubjectLabel, type ApprovalRequestInfo} from 'model/approval';

/** The API refuses anything shorter; the same limit is checked here up front. */
export const REJECT_REASON_MIN_LENGTH = 10;

/** Prefill offered by the design -- the API has no canned reasons, this is just text. */
const QUICK_REASONS = ['duplicate', 'outOfPlan', 'noBudget', 'internalAvailable'];

interface RejectFormValues {
    reason: string;
}

export const ApprovalRejectDialog = ({item, onSuccess, onClose}: {
    item: ApprovalRequestInfo,
    onSuccess: () => void,
    onClose: () => void
}) => {
    const t = useAppTranslation();
    const dispatch = useAppDispatch();
    const [isSaving, setIsSaving] = useState(false);
    /** kept on the dialog, not in a toast -- see ApprovalApproveDialog */
    const [error, setError] = useState<string>();

    const handleValidate = useCallback((values: RejectFormValues) => {
        const errors: FormikErrors<RejectFormValues> = {};
        const reason = values.reason.trim();
        if (!reason) {
            errors.reason = t('form.required');
        } else if (reason.length < REJECT_REASON_MIN_LENGTH) {
            errors.reason = t('approvals.reject.reasonTooShort', {min: REJECT_REASON_MIN_LENGTH});
        }
        return errors;
    }, [t]);

    const handleSubmit = useCallback(async (values: RejectFormValues) => {
        setIsSaving(true);
        setError(undefined);
        const result = await dispatch(rejectApprovalRequest({
            id: String(item.id),
            body: {reason: values.reason.trim()}
        }));
        setIsSaving(false);

        if (isApiResultError(result)) {
            setError(approvalDecisionError(result.payload as any, t));
            return;
        }
        dispatch(addMessage({severity: 'success', title: t('approvals.reject.done')}));
        onSuccess();
        onClose();
    }, [item.id, dispatch, onSuccess, onClose, t]);

    return (
        <Dialog open={true} onClose={onClose} PaperComponent={Paper} fullWidth maxWidth={'sm'}>
            <DialogTitle>{t('approvals.reject.title', {
                subject: approvalSubjectLabel(item),
                name: item.user?.full_name
            })}</DialogTitle>
            <LinearProgress hidden={!isSaving}/>
            <Formik initialValues={{reason: ''}} onSubmit={handleSubmit} validate={handleValidate}>
                {({handleSubmit: submit, setFieldValue}) => <form onSubmit={submit}>
                    <DialogContent>
                        <p className={'tw-text-12 tw-mb-8'}>{t('approvals.reject.quickReasons')}</p>
                        <div className={'tw-flex tw-flex-wrap tw-gap-8 tw-mb-16'}>
                            {QUICK_REASONS.map(key => {
                                const label = t('approvals.reject.reason.' + key);
                                return <Chip key={key} label={label} variant={'outlined'} size={'small'}
                                    onClick={() => setFieldValue('reason', label)}/>;
                            })}
                        </div>
                        <TextFormField name={'reason'} required type={'textarea'}
                            label={t('approvals.reject.reasonLabel')}
                            helperText={t('approvals.reject.reasonHint', {min: REJECT_REASON_MIN_LENGTH})}
                            minRows={1} maxRows={6} maxlength={1000}/>
                        {!!error && <Alert severity={'error'} className={'tw-mt-16'}
                            onClose={() => setError(undefined)}>{error}</Alert>}
                    </DialogContent>
                    <DialogActions sx={{padding: '16px'}}>
                        <Button color={'inherit'} onClick={onClose} disabled={isSaving}>{t('form.cancel')}</Button>
                        {/* muted red, as drawn -- rejection should not read louder
                            than the green approve on the queue */}
                        <Button variant={'contained'} type={'submit'} disabled={isSaving}
                            sx={{
                                background: 'var(--color-error-back)',
                                color: '#A32409',
                                '&:hover': {background: '#FBD5CE'},
                            }}>
                            {t('approvals.reject.confirm')}
                        </Button>
                    </DialogActions>
                </form>}
            </Formik>
        </Dialog>
    );
};
